/** Which regulator's dates are worth reminding a person of in Action items.
 *
 * A reminder is **a key date of a regime that is law now and applies to a company in this workspace**:
 * an obligation that is `required`, determined, the current version for its entity and year, under a
 * regime with no in-force caveat. A regime that is only proposed or is enjoined is never reminded of,
 * for the reason `clock_notifications` never counts down to one. Nothing is computed from a date and
 * nothing is counted down to: the rows are shown as they are, once per regime, with their sources.
 */

import type { KeyDate } from "../lib/keyDates";
import type { Obligation } from "../api/queries";

export interface DatesGroup {
  regimeKey: string;
  regimeName: string;
  items: KeyDate[];
}

export function reminderDates(obligations: readonly Obligation[]): DatesGroup[] {
  const byRegime = new Map<string, DatesGroup>();
  for (const obligation of obligations) {
    if (
      obligation.status !== "required" ||
      obligation.undetermined ||
      obligation.superseded_by_obligation_id !== null ||
      obligation.in_force_caveat !== null ||
      obligation.key_dates.length === 0
    ) {
      continue;
    }
    const group = byRegime.get(obligation.regime_key) ?? {
      regimeKey: obligation.regime_key,
      regimeName: obligation.regime_name,
      items: [],
    };
    for (const item of obligation.key_dates) {
      if (!group.items.some((held) => held.key === item.key)) group.items.push(item);
    }
    byRegime.set(obligation.regime_key, group);
  }
  return [...byRegime.values()].sort((a, b) => a.regimeKey.localeCompare(b.regimeKey));
}
